import { LexicalEditor } from "lexical"
import { parse } from "node-html-parser"
import { RESIZE_HANDLE_WRAPPER_CLASS } from "./plugins/InlineImagePlugin/EditableImage/EditableImage"
import { EDITOR_BODY_CLASSNAME } from "platform-common/util/string"
import { INLINE_IMAGE_CLASSNAME } from "./plugins/InlineImagePlugin/InlineImageNode"

export const getLexicalHtmlWithVariableValues = (
  htmlWithVariablePlaceholders: string,
  values: Record<string, string | string[]>
) => {
  const parser = new DOMParser()
  const dom = parser.parseFromString(htmlWithVariablePlaceholders, "text/html")

  Object.entries(values).forEach(([variable, value]) => {
    const elements = dom.querySelectorAll(`[data-beautiful-mention="{{${variable}}}"]`)

    if (typeof value === "string") {
      elements.forEach((e) => {
        e.replaceWith(value)
      })
    } else {
      elements.forEach((e, i) => {
        const v = value[i % value.length]
        if (v) {
          e.replaceWith(v)
        }
      })
    }
  })

  return dom.body.outerHTML
}

export const hasUnfilledVariables = (html: string) => {
  const dom = parse(html || "<div/>")
  const hasVariables = dom.querySelectorAll("[data-beautiful-mention]").length > 0

  return hasVariables
}

export const fixEditorHtml = (editor: LexicalEditor, singleLine: boolean) => {
  let html = editor.getRootElement()?.innerHTML || ""

  // Make sure the content is within a single line
  if (singleLine) html = html.replace(/<br\/*>/g, "")

  const dom = new DOMParser().parseFromString(html, "text/html")

  // Mark the HTML as one edited through the lexical editor
  dom.body.classList.add(EDITOR_BODY_CLASSNAME)

  // Make sure whitespaces are preserved in empty spans
  Array.from(getTextSpans(dom)).forEach((span) => {
    span.innerHTML = span.innerHTML.replace(/\s/g, "&nbsp;")
  })

  // Make sure the floating editor is not persisted
  dom.querySelectorAll("form").forEach((form) => form.remove())

  // Clean up images

  // Remove image resize handles
  const resizeHandles = Array.from(dom.querySelectorAll(`.${RESIZE_HANDLE_WRAPPER_CLASS}`)).concat(
    Array.from(dom.querySelectorAll('[style*="col-resize"]'))
  )
  resizeHandles.forEach((handle) => handle.remove())

  dom.querySelectorAll(`.${INLINE_IMAGE_CLASSNAME}`).forEach((image) => {
    const next = image.nextElementSibling
    const imgTags = image.querySelectorAll("img")

    // Remove redundant line break right after the image
    next?.tagName === "BR" && next.remove()

    // Remove image outlines that may persist from user focusing an editable image
    imgTags.forEach((img) => (img.style.outline = "none"))
  })

  return dom.body.outerHTML
}

/**
 * Normalizes spaces in HTML content from a text editor by replacing non-breaking spaces
 * with regular spaces. This is especially useful for ensuring consistent spacing in text
 * before processing outside of the Lexical editor.
 */
export const normalizeSpacesInEditorHtml = (html: string): string => {
  const dom = new DOMParser().parseFromString(html, "text/html")

  // Replacing non-breaking spaces with regular spaces in all text spans
  Array.from(getTextSpans(dom)).forEach((span) => {
    span.innerHTML = span.innerHTML.replaceAll("&nbsp;", " ")
  })

  return dom.body.outerHTML
}

/**
 * Returns all text spans in the given DOM that are marked as lexical text (text entries).
 */
const getTextSpans = (dom: Document) => dom.querySelectorAll('span[data-lexical-text="true"]')
